import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../../supabaseClient";
import { loadScoringHubData } from "../../../utility/loadScoringHubData";
import { attachLaneFields } from "../../../utility/laneAssignmentApi";
import { attachRecapSheetFields } from "../../../utility/recapSheets";

export const useTournamentData = (tournamentId) => {
  // Basic state management
  const [data, setData] = useState({
    teams: [],
    squads: [],
    tournament: null,
    pagination: { page: 1, limit: 10, total: 0 },
  });
  const [allTeams, setAllTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Calculation cache state - we need this for initial totals
  const [calculationResults, setCalculationResults] = useState({});

  // Data fetching logic
  const fetchTournamentData = useCallback(async () => {
    if (!tournamentId) return;

    setLoading(true);
    setError(null);

    try {
      const invoked = await loadScoringHubData(tournamentId);

      const response = await attachRecapSheetFields(
        await attachLaneFields(invoked)
      );

      // Fetch tournament finalization status from the new verification system
      const { data: verificationStates, error: statusError } = await supabase
        .from("verificationStates")
        .select("*")
        .eq("tournamentId", tournamentId);

      if (statusError) {
        console.warn(
          "Could not fetch tournament verification status:",
          statusError
        );
      }

      // Extract verification status for different entity types
      // Handle case where verificationStates might be null/empty
      const verificationStatesArray = verificationStates || [];

      const isPayoutFinalized =
        verificationStatesArray.find(
          (vs) => vs.entityType === "payouts" && vs.isVerified
        )?.isVerified || false;
      const isResultsFinalized =
        verificationStatesArray.find(
          (vs) => vs.entityType === "results" && vs.isVerified
        )?.isVerified || false;
      const isTournamentFinalized =
        verificationStatesArray.find(
          (vs) => vs.entityType === "tournament" && vs.isVerified
        )?.isVerified || false;

      // Store all teams for client-side filtering
      setAllTeams(response.teams || []);

      // Transform the data to include verification status and transformed scores
      const transformedTeams = (response.teams || []).map((team) => {
        // Get team verification status from the new verification system
        const teamVerification = verificationStates?.find(
          (vs) => vs.entityType === "scores" && vs.entityId === team.id
        );

        const updatedBowlers =
          team.bowlers?.map((bowler) => {
            const bowlerVerification = verificationStates?.find(
              (vs) => vs.entityType === "averages" && vs.entityId === bowler.id
            );

            return {
              ...bowler,
              isVerified: bowlerVerification?.isVerified || false,
            };
          }) || team.bowlers;

        return {
          ...team,
          isVerified: teamVerification?.isVerified || false,
          verifiedAt: teamVerification?.verifiedAt || null,
          bowlers: updatedBowlers,
          transformedScores:
            team.bowlers?.reduce((acc, bowler) => {
              const bowlerScores = [];
              // Initialize with the correct number of games
              for (let i = 0; i < (team.gamesPerEvent || 3); i++) {
                bowlerScores[i] = {
                  id: `${bowler.id}-${i}`,
                  gameNumber: i + 1,
                  score: null,
                  bowlerID: bowler.id,
                  teamID: team.id,
                };
              }

              // Populate scores from the scores table
              if (team.scores && Array.isArray(team.scores)) {
                team.scores.forEach((score) => {
                  if (score.bowlerID === bowler.id && score.gameNumber) {
                    const gameIndex = score.gameNumber - 1;
                    if (gameIndex >= 0 && gameIndex < bowlerScores.length) {
                      bowlerScores[gameIndex] = {
                        ...score,
                        id: score.id || `${bowler.id}-${gameIndex}`,
                      };
                    }
                  }
                });
              }

              acc[bowler.id] = bowlerScores;
              return acc;
            }, {}) || {},
        };
      });

      // Initialize calculation cache from stored calculate-scores rows
      const initialCacheUpdates = {
        ...(response.calculationResults || {}),
      };

      (response.teams || []).forEach((team) => {
        // Store team totals in cache
        if (team.teamTotals) {
          const teamScratchKey = `${team.id}-team-scratch`;
          const teamHandicapKey = `${team.id}-team-handicap`;

          initialCacheUpdates[teamScratchKey] = {
            teamScratchTotal: team.teamTotals.scratchTotal || 0,
          };
          initialCacheUpdates[teamHandicapKey] = {
            teamHandicapTotal: team.teamTotals.handicapTotal || 0,
          };
        }

        // Store individual bowler totals in cache
        if (team.bowlers) {
          team.bowlers.forEach((bowler) => {
            if (
              bowler.scratchTotal !== undefined ||
              bowler.handicapTotal !== undefined
            ) {
              const bowlerScratchKey = `${team.id}-${bowler.id}-scratch`;
              const bowlerHandicapKey = `${team.id}-${bowler.id}-handicap`;

              initialCacheUpdates[bowlerScratchKey] = {
                total: bowler.scratchTotal || 0,
                scratchTotal: bowler.scratchTotal || 0,
              };
              initialCacheUpdates[bowlerHandicapKey] = {
                handicapTotal: bowler.handicapTotal || 0,
                handicap: bowler.handicap || 0,
              };
            }
          });
        }
      });

      // Apply all initial cache updates
      if (Object.keys(initialCacheUpdates).length > 0) {
        setCalculationResults((prev) => ({ ...prev, ...initialCacheUpdates }));
      }

      setData({
        teams: transformedTeams,
        tournament: {
          ...(response.tournament || {}),
          isPayoutFinalized,
          isResultsFinalized,
          isTournamentFinalized,
        },
        squads: response.squads || [],
        events: response.events || [],
        pagination: response.pagination || {
          currentPage: 1,
          totalPages: 1,
          totalTeams: 0,
          hasNextPage: false,
          hasPrevPage: false,
        },
      });
    } catch (err) {
      console.error("Error fetching tournament data:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tournamentId]);

  // Initial fetch
  useEffect(() => {
    fetchTournamentData();
  }, [fetchTournamentData]);

  return {
    data,
    allTeams,
    loading,
    error,
    refetch: fetchTournamentData,
    calculationResults,
  };
};
